import { useState } from 'react';
import { noteName } from '@/core/theory/key';
import { useTuner } from '@/features/tuner';
import { buttonClass } from '@/ui/button';
import { Segmented } from '@/ui/Segmented';
import styles from './TunerPage.module.css';

/** Open strings, lowest first, as MIDI notes. */
const STRINGS = {
  guitar: [40, 45, 50, 55, 59, 64],
  ukulele: [67, 60, 64, 69],
  chromatic: [],
} as const;

type Mode = keyof typeof STRINGS;

const MODES: { value: Mode; label: string }[] = [
  { value: 'guitar', label: 'Guitar' },
  { value: 'ukulele', label: 'Ukulele' },
  { value: 'chromatic', label: 'Chromatic' },
];

/** Within this many cents, the note counts as in tune. */
const IN_TUNE = 5;

const nameOf = (midi: number) => noteName(midi, false);
const octaveOf = (midi: number) => Math.floor(midi / 12) - 1;

/** Chromatic tuner with the microphone (F9.2). */
export function TunerPage() {
  const { status, reading, start, stop } = useTuner();
  const [mode, setMode] = useState<Mode>('guitar');
  const listening = status === 'listening';
  const cents = reading ? Math.round(reading.cents) : 0;
  const inTune = reading !== null && Math.abs(cents) <= IN_TUNE;
  const strings = STRINGS[mode];

  return (
    <main className={styles.page}>
      <h1 className={styles.title}>Tuner</h1>
      <p className={styles.lead}>
        Uses your microphone. The sound is analyzed on this device; nothing is recorded or sent
        anywhere.
      </p>

      <section aria-label="Tuner" className={styles.card}>
        <Segmented label="Instrument" options={MODES} value={mode} onChange={setMode} />

        <div className={styles.display} data-in-tune={inTune || undefined}>
          <div className={styles.note} aria-live="polite" data-testid="tuner-note">
            {reading ? (
              <>
                {nameOf(reading.midi)}
                <span className={styles.octave}>{octaveOf(reading.midi)}</span>
              </>
            ) : (
              '–'
            )}
          </div>
          <div
            className={styles.meter}
            role="meter"
            aria-label="Cents from the note"
            aria-valuemin={-50}
            aria-valuemax={50}
            aria-valuenow={cents}
            aria-valuetext={
              reading ? (inTune ? 'In tune' : `${cents > 0 ? '+' : ''}${cents} cents`) : 'No note'
            }
          >
            <span className={styles.center} />
            {reading && <span className={styles.needle} style={{ left: `${50 + cents}%` }} />}
          </div>
          <div className={styles.readout}>
            {reading ? (
              <>
                <span>
                  {inTune ? 'In tune' : cents < 0 ? `${-cents} cents flat` : `${cents} cents sharp`}
                </span>
                <span className={styles.hz}>{reading.frequency.toFixed(1)} Hz</span>
              </>
            ) : (
              <span>{listening ? 'Play a single string…' : ' '}</span>
            )}
          </div>
        </div>

        {strings.length > 0 && (
          <ul className={styles.strings} aria-label="Open strings">
            {strings.map((midi, i) => (
              <li
                key={i}
                className={styles.string}
                data-active={reading?.midi === midi || undefined}
              >
                {nameOf(midi)}
                <span className={styles.octave}>{octaveOf(midi)}</span>
              </li>
            ))}
          </ul>
        )}

        <div className={styles.actions}>
          {listening ? (
            <button type="button" className={buttonClass('secondary')} onClick={stop}>
              Stop
            </button>
          ) : (
            <button
              type="button"
              className={buttonClass('primary')}
              disabled={status === 'starting'}
              onClick={() => void start()}
            >
              {status === 'starting' ? 'Starting…' : 'Start tuner'}
            </button>
          )}
          {status === 'denied' && (
            <p role="alert" className={styles.error}>
              Microphone access was blocked. Allow it in the browser&apos;s site settings (the icon
              next to the address), then try again.
            </p>
          )}
          {status === 'unavailable' && (
            <p role="alert" className={styles.error}>
              No microphone is available in this browser.
            </p>
          )}
        </div>
      </section>
    </main>
  );
}
